import { DocumentStatus } from '@/types/enums/DocumentStatus';
import { SignStatus } from '@/types/enums/SignStatus';
import type { Document } from '@/types/Document';
import type { Signee } from '@/types/Signee';
import type { OverviewDocument } from '@/stores/overview.ts';
import type { AutographPosition } from '@/types/AutographPosition';
import { SigneeType } from '@/types/enums/SigneeType';

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface PDFPageProxy {
  [key: string]: any;
}

class SignatureService {
  canSign = (email: string, document: Document): boolean =>
    (document?.signees?.some(
      (signee) =>
        this.compareString(signee.email, email) &&
        signee.signStatus !== SignStatus.SIGNED &&
        signee.signeeType === SigneeType.SIGN &&
        signee.signStatus !== SignStatus.ON_HOLD
    ) &&
      document.documentStatus === DocumentStatus.IN_PROGRESS) ??
    false;

  canApprove = (email: string, document: Document): boolean =>
    (document?.signeesOrdered
      .flat()
      ?.some(
        (signee) =>
          this.compareString(signee.email, email) &&
          signee.signeeType === SigneeType.APPROVE &&
          (signee.signStatus === SignStatus.PENDING ||
            signee.signStatus === SignStatus.ON_HOLD)
      ) &&
      document.documentStatus === DocumentStatus.IN_PROGRESS) ??
    false;

  isSignedByUser = (userEmail: string, signees: Signee[]): boolean =>
    signees
      .filter((signee) => this.compareString(signee.email, userEmail))
      .every((userSignature) => userSignature.signStatus === SignStatus.SIGNED);

  isSignedByUserFromOverview = (document: OverviewDocument) =>
    document?.signStatus === SignStatus.SIGNED;

  shouldISign = (userEmail: string, signees: Signee[]) =>
    !!signees.find((signee) => this.compareString(signee.email, userEmail));

  shouldISignFromOverview = (overviewDocument: OverviewDocument) =>
    overviewDocument?.signStatus === SignStatus.PENDING;

  getSignatureByEmail = (userEmail: string, signatures: Signee[]) =>
    signatures.find((signature) =>
      this.compareString(signature.email, userEmail)
    );

  scrollToSignaturePosition(autographPosition: AutographPosition) {
    if (!autographPosition) {
      return;
    }

    const documentPreview = document.getElementById('page-content');
    const currentPage = document.getElementById(
      `id-${autographPosition.pageNumber}`
    );

    if (!currentPage || !documentPreview) {
      return;
    }

    const heightRatio = currentPage.clientHeight / 842;
    const positionY = Math.round(
      currentPage.clientHeight - autographPosition.y * heightRatio
    );
    const halfViewportHeight = window.innerHeight / 2;

    documentPreview.scroll({
      top:
        currentPage.offsetTop +
        positionY -
        autographPosition.height * heightRatio -
        halfViewportHeight,
      behavior: 'smooth'
    });
  }

  compareString(first: string, second: string, insensitive = true): boolean {
    if (!first || !second) {
      return false;
    }
    return insensitive
      ? first.toLowerCase() === second.toLowerCase()
      : first === second;
  }

  getSigneeByEmail = (email: string, signees: Signee[]) => {
    return signees.find((signee) => this.compareString(signee.email, email));
  };

  onlyMe(userEmail: string, document: Document) {
    return !!(
      document?.signeesOrdered &&
      this.getSigneeByEmail(userEmail, document.signeesOrdered.flat())
    );
  }
}

export default new SignatureService();
